<script setup>
// SiteUp CRM (2026-09-19): "Enviar no chat interno" do menu Compartilhar.
// A pessoa escolhe um colega ou uma sala, escreve uma linha opcional e a
// mensagem sai com a URL canônica do cartão, que o chat interno já renderiza
// como card. Acesso do destinatário nunca é concedido em silêncio.
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import agentsAPI from 'dashboard/api/agents';
import internalChatRooms from 'dashboard/api/internalChatRooms';
import kanbanSharedAccessAPI from 'dashboard/api/kanbanSharedAccess';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  RECIPIENT_ACCESS,
  buildCardShareMessage,
  buildCardShareUrl,
  resolveRecipientAccess,
} from '../helpers/shareCard';

const props = defineProps({
  itemId: {
    type: [Number, String],
    required: true,
  },
  itemTitle: {
    type: String,
    default: '',
  },
  item: {
    type: Object,
    default: null,
  },
  canManage: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'accessGranted']);

const { t } = useI18n();
const store = useStore();
const { accountId, accountScopedRoute } = useAccount();
const currentUser = useMapGetter('getCurrentUser');

const dialogRef = ref(null);
const searchInputRef = ref(null);
const isLoading = ref(false);
const isSending = ref(false);
const loadError = ref(false);
const sendErrorKey = ref('');
const agents = ref([]);
const rooms = ref([]);
const sharedAccessUserIds = ref([]);
const searchTerm = ref('');
const selectedKey = ref('');
const note = ref('');
const grantAccess = ref(true);

const funnel = computed(() => {
  const funnelId = props.item?.funnel_id ?? props.item?.funnel?.id;
  if (!funnelId) return null;
  const list = store.getters['funnel/getFunnels'] || [];
  return list.find(entry => Number(entry.id) === Number(funnelId)) || null;
});

const shareUrl = computed(() =>
  buildCardShareUrl({
    accountId: accountId.value,
    itemId: props.itemId,
    origin: window.location.origin,
  })
);

const peopleTargets = computed(() =>
  agents.value
    .filter(agent => Number(agent.id) !== Number(currentUser.value?.id))
    .map(agent => ({
      key: `user-${agent.id}`,
      type: 'user',
      id: Number(agent.id),
      name: agent.name || agent.email,
      subtitle: agent.email || '',
      avatarUrl: agent.avatar_url || '',
      user: agent,
    }))
);

const roomTargets = computed(() =>
  rooms.value
    .filter(room => room.kind !== 'direct')
    .map(room => ({
      key: `room-${room.id}`,
      type: 'room',
      id: Number(room.id),
      name: room.display_name || room.name || `#${room.id}`,
      subtitle: t('KANBAN_SHARING.SEND.ROOM_SUBTITLE'),
      avatarUrl: '',
    }))
);

const targets = computed(() => [...peopleTargets.value, ...roomTargets.value]);

const filteredTargets = computed(() => {
  const term = searchTerm.value.trim().toLowerCase();
  if (!term) return targets.value;
  return targets.value.filter(target =>
    `${target.name} ${target.subtitle}`.toLowerCase().includes(term)
  );
});

const selectedTarget = computed(
  () => targets.value.find(target => target.key === selectedKey.value) || null
);

const recipientAccess = computed(() => {
  const target = selectedTarget.value;
  if (!target || target.type !== 'user') return null;
  return resolveRecipientAccess({
    recipient: target.user,
    item: props.item,
    funnel: funnel.value,
    sharedAccessUserIds: sharedAccessUserIds.value,
    senderCanManage: props.canManage,
  });
});

const canOfferAccess = computed(
  () => recipientAccess.value === RECIPIENT_ACCESS.NEEDS_ACCESS
);
const showUnknownAccessNotice = computed(
  () => recipientAccess.value === RECIPIENT_ACCESS.UNKNOWN
);
const isRoomSelected = computed(() => selectedTarget.value?.type === 'room');

const sendErrorMessage = computed(() => {
  if (!sendErrorKey.value) return '';
  return t(sendErrorKey.value, { name: selectedTarget.value?.name || '' });
});

const canSend = computed(
  () => Boolean(selectedTarget.value) && Boolean(shareUrl.value)
);

const loadSharedAccesses = async () => {
  if (!props.canManage) {
    sharedAccessUserIds.value = [];
    return;
  }
  const response = await kanbanSharedAccessAPI.get(props.itemId);
  const payload = response?.data?.payload ?? response?.data ?? {};
  sharedAccessUserIds.value = (
    Array.isArray(payload.shared_accesses) ? payload.shared_accesses : []
  ).map(access => Number(access.user_id));
};

const load = async () => {
  isLoading.value = true;
  loadError.value = false;
  try {
    const [agentsResponse, roomsResponse] = await Promise.all([
      agentsAPI.get(),
      internalChatRooms.get({ kind: 'multi_user' }),
    ]);
    const agentsPayload =
      agentsResponse?.data?.payload ?? agentsResponse?.data ?? [];
    agents.value = Array.isArray(agentsPayload) ? agentsPayload : [];
    const roomsPayload = roomsResponse?.data;
    const list = Array.isArray(roomsPayload)
      ? roomsPayload
      : roomsPayload?.rooms;
    rooms.value = Array.isArray(list) ? list : [];
    await loadSharedAccesses();
  } catch {
    loadError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const open = async () => {
  searchTerm.value = '';
  selectedKey.value = '';
  note.value = '';
  grantAccess.value = true;
  sendErrorKey.value = '';
  dialogRef.value?.open();
  load();
  await nextTick();
  searchInputRef.value?.focus();
};

const close = () => dialogRef.value?.close();

const selectTarget = key => {
  selectedKey.value = key;
  sendErrorKey.value = '';
};

const resolveRoomId = async target => {
  if (target.type === 'room') return target.id;
  const { data } = await internalChatRooms.create({
    room: { kind: 'direct', user_id: target.id },
  });
  return data?.id;
};

const notifySent = roomId => {
  useAlert(t('KANBAN_SHARING.SEND.SENT'), {
    type: 'link',
    to: accountScopedRoute('internal_chat', {}, { room: String(roomId) }),
    message: t('KANBAN_SHARING.SEND.OPEN_CONVERSATION'),
  });
};

const send = async () => {
  const target = selectedTarget.value;
  if (!target || isSending.value || !shareUrl.value) return;
  isSending.value = true;
  sendErrorKey.value = '';

  const shouldGrant = canOfferAccess.value && grantAccess.value;
  let granted = false;

  try {
    if (shouldGrant) {
      await kanbanSharedAccessAPI.create(props.itemId, {
        user_id: target.id,
        access_level: 'view',
      });
      granted = true;
      sharedAccessUserIds.value = [...sharedAccessUserIds.value, target.id];
      emit('accessGranted', { userId: target.id, accessLevel: 'view' });
    }
  } catch {
    sendErrorKey.value = 'KANBAN_SHARING.SEND.ERRORS.GRANT';
    isSending.value = false;
    return;
  }

  try {
    const roomId = await resolveRoomId(target);
    if (!roomId) throw new Error('room_unavailable');
    await internalChatRooms.sendMessage(
      roomId,
      buildCardShareMessage({ note: note.value, url: shareUrl.value })
    );
    notifySent(roomId);
    close();
  } catch {
    sendErrorKey.value = granted
      ? 'KANBAN_SHARING.SEND.ERRORS.SEND_AFTER_GRANT'
      : 'KANBAN_SHARING.SEND.ERRORS.SEND';
  } finally {
    isSending.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="xl"
    overflow-y-auto
    content-tag="div"
    :title="t('KANBAN_SHARING.SEND.TITLE')"
    :description="itemTitle || t('KANBAN_SHARING.SEND.DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="emit('close')"
  >
    <div
      v-if="isLoading"
      class="flex min-h-40 items-center justify-center text-sm text-n-slate-11"
      role="status"
    >
      <span class="i-ph-spinner-gap mr-2 size-5 animate-spin" />
      {{ t('KANBAN_SHARING.SEND.LOADING') }}
    </div>

    <div
      v-else-if="loadError"
      class="rounded-xl border border-n-ruby-6 bg-n-ruby-3 p-4 text-sm text-n-ruby-11"
      role="alert"
    >
      <p class="m-0">{{ t('KANBAN_SHARING.SEND.ERRORS.LOAD') }}</p>
      <Button
        class="mt-3"
        type="button"
        color="slate"
        variant="outline"
        size="sm"
        :label="t('KANBAN_SHARING.ACTIONS.RETRY')"
        @click="load"
      />
    </div>

    <div v-else class="space-y-4">
      <input
        ref="searchInputRef"
        v-model="searchTerm"
        type="search"
        class="h-10 w-full rounded-xl border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
        :placeholder="t('KANBAN_SHARING.SEND.SEARCH_PLACEHOLDER')"
        :aria-label="t('KANBAN_SHARING.SEND.SEARCH_LABEL')"
      />

      <p
        v-if="!filteredTargets.length"
        class="m-0 rounded-xl bg-n-alpha-2 p-3 text-sm text-n-slate-11"
      >
        {{ t('KANBAN_SHARING.SEND.EMPTY') }}
      </p>

      <ul
        v-else
        class="m-0 max-h-56 list-none space-y-1 overflow-y-auto p-0"
        :aria-label="t('KANBAN_SHARING.SEND.TARGETS_LABEL')"
      >
        <li v-for="target in filteredTargets" :key="target.key">
          <button
            type="button"
            class="flex w-full items-center gap-3 rounded-xl border px-3 py-2 text-left transition-colors"
            :class="
              selectedKey === target.key
                ? 'border-n-blue-8 bg-n-blue-3'
                : 'border-transparent hover:bg-n-alpha-2'
            "
            :aria-pressed="selectedKey === target.key"
            :data-testid="`kanban-share-target-${target.key}`"
            @click="selectTarget(target.key)"
          >
            <Avatar
              v-if="target.type === 'user'"
              :name="target.name"
              :src="target.avatarUrl"
              :size="28"
              rounded-full
            />
            <span
              v-else
              class="i-ph-users-three size-7 shrink-0 text-n-slate-11"
            />
            <span class="min-w-0 flex-1">
              <span class="block truncate text-sm text-n-slate-12">
                {{ target.name }}
              </span>
              <span class="block truncate text-xs text-n-slate-11">
                {{ target.subtitle }}
              </span>
            </span>
          </button>
        </li>
      </ul>

      <label class="block space-y-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('KANBAN_SHARING.SEND.NOTE_LABEL') }}
        </span>
        <textarea
          v-model="note"
          rows="2"
          class="w-full rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          :placeholder="t('KANBAN_SHARING.SEND.NOTE_PLACEHOLDER')"
        />
      </label>

      <div
        v-if="canOfferAccess"
        class="rounded-xl border border-n-amber-6 bg-n-amber-3 p-3 text-sm text-n-amber-11"
        data-testid="kanban-share-needs-access"
      >
        <p class="m-0">
          {{
            t('KANBAN_SHARING.SEND.NO_ACCESS', {
              name: selectedTarget?.name || '',
            })
          }}
        </p>
        <label class="mt-2 flex items-center gap-2">
          <input
            v-model="grantAccess"
            type="checkbox"
            data-testid="kanban-share-grant-access"
          />
          <span>{{ t('KANBAN_SHARING.SEND.GRANT_VIEW') }}</span>
        </label>
        <p class="m-0 mt-1 text-xs">
          {{ t('KANBAN_SHARING.SEND.SEND_ANYWAY_HELP') }}
        </p>
      </div>

      <p
        v-if="showUnknownAccessNotice"
        class="m-0 rounded-xl border border-n-amber-6 bg-n-amber-3 p-3 text-sm text-n-amber-11"
        data-testid="kanban-share-unknown-access"
      >
        {{
          t('KANBAN_SHARING.SEND.NO_ACCESS_NO_MANAGE', {
            name: selectedTarget?.name || '',
          })
        }}
      </p>

      <p
        v-if="isRoomSelected"
        class="m-0 rounded-xl bg-n-alpha-2 p-3 text-sm text-n-slate-11"
        data-testid="kanban-share-room-notice"
      >
        {{ t('KANBAN_SHARING.SEND.ROOM_ACCESS_NOTICE') }}
      </p>

      <p
        v-if="sendErrorKey"
        class="m-0 rounded-xl border border-n-ruby-6 bg-n-ruby-3 p-3 text-sm text-n-ruby-11"
        role="alert"
        data-testid="kanban-share-send-error"
      >
        {{ sendErrorMessage }}
      </p>
    </div>

    <template #footer>
      <div class="flex w-full items-center justify-end gap-2">
        <Button
          type="button"
          color="slate"
          variant="faded"
          :disabled="isSending"
          :label="t('KANBAN_SHARING.ACTIONS.CANCEL')"
          @click="close"
        />
        <Button
          v-if="!isLoading && !loadError"
          type="button"
          data-testid="kanban-share-send"
          :is-loading="isSending"
          :disabled="!canSend || isSending"
          :label="t('KANBAN_SHARING.SEND.CONFIRM')"
          @click="send"
        />
      </div>
    </template>
  </Dialog>
</template>
